import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Books, Barbell, Lightning, Timer, ArrowsClockwise, Heart, Trophy, MagnifyingGlass,
  Plus, X, VideoCamera, CheckCircle, Trash, ClipboardText, Play,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { normalizeVideoUrl, isVideoHostUrl } from "@/lib/videoUrl";
import { Input } from "@/components/ui/input";
import LogScoreModal from "@/components/LogScoreModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: "all",            label: "All",            icon: Books },
  { id: "mobility",       label: "Mobility",       icon: ArrowsClockwise },
  { id: "strength",       label: "Strength",       icon: Barbell },
  { id: "power",          label: "Power",          icon: Lightning },
  { id: "speed",          label: "Speed",          icon: Timer },
  { id: "agility",        label: "Agility",        icon: ArrowsClockwise },
  { id: "conditioning",   label: "Conditioning",   icon: Heart },
  { id: "sport_specific", label: "Sport-Specific", icon: Trophy },
];

const resolveMedia = (u) => {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return normalizeVideoUrl(u);
  if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
  return u;
};

export default function AthleteTestLibrary() {
  const [rows, setRows] = useState([]);
  const [results, setResults] = useState([]);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [scoreOpen, setScoreOpen] = useState(null); // test being logged
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [{ data: tests }, { data: hist }] = await Promise.all([
        api.get("/athlete/test-library"),
        api.get("/athlete/test-results"),
      ]);
      setRows(tests || []);
      setResults(hist || []);
    } catch (e) {
      toast.error("Failed to load tests");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resultsByTest = useMemo(() => {
    const m = {};
    results.forEach((r) => {
      (m[r.test_id] ||= []).push(r);
    });
    return m;
  }, [results]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (cat !== "all" && r.category !== cat) return false;
      if (q && !((r.name || "").toLowerCase().includes(q.toLowerCase()) || (r.sport || "").toLowerCase().includes(q.toLowerCase()))) return false;
      return true;
    });
  }, [rows, cat, q]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((r) => { (g[r.category || "other"] ||= []).push(r); });
    return g;
  }, [filtered]);

  return (
    <div className="max-w-7xl mx-auto p-6 lg:p-10 space-y-6" data-testid="athlete-test-library-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-tiny text-brand-accent">// ATHLETE TOOLKIT</div>
          <h1 className="font-heading text-4xl sm:text-5xl">Test Library</h1>
          <p className="text-text-secondary text-sm mt-1">
            Standard assessments with reference videos. Log your own scores to track progress over time.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="border border-white/10 px-3 py-2">
            <div className="label-tiny text-text-secondary">MY RESULTS</div>
            <div className="font-heading text-2xl tabular-nums" data-testid="results-count">{results.length}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 border-b border-white/10 pb-3" data-testid="test-lib-filters">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-secondary" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search tests..."
            className="pl-7"
            data-testid="test-lib-search"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setCat(c.id)}
              data-testid={`test-lib-cat-${c.id}`}
              className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs label-tiny tracking-wider border transition ${
                cat === c.id
                  ? "bg-brand-accent text-surface-base border-brand-accent"
                  : "text-text-secondary border-white/10 hover:border-white/30"
              }`}
            >
              <c.icon size={12} weight="duotone" /> {c.label.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="label-tiny text-text-secondary">LOADING...</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="border border-white/10 p-10 text-center text-text-secondary" data-testid="test-lib-empty">
          No tests match your filters.
        </div>
      ) : (
        Object.entries(grouped).map(([category, list]) => (
          <div key={category} className="space-y-3" data-testid={`test-lib-group-${category}`}>
            <div className="label-tiny text-brand-accent tracking-widest">// {category.replace("_", " ").toUpperCase()}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {list.map((t) => (
                <TestCard
                  key={t.test_id}
                  test={t}
                  results={resultsByTest[t.test_id] || []}
                  onOpen={() => setSelected(t)}
                  onLog={() => setScoreOpen(t)}
                />
              ))}
            </div>
          </div>
        ))
      )}

      {selected && (
        <TestDetailModal
          test={selected}
          results={resultsByTest[selected.test_id] || []}
          onClose={() => setSelected(null)}
          onLog={() => { setScoreOpen(selected); setSelected(null); }}
          onDeleteResult={async (result_id) => {
            try {
              await api.delete(`/athlete/test-results/${result_id}`);
              toast.success("Result removed");
              await load();
            } catch { toast.error("Failed"); }
          }}
        />
      )}
      {scoreOpen && (
        <LogScoreModal
          test={scoreOpen}
          onClose={() => setScoreOpen(null)}
          onSaved={async () => { setScoreOpen(null); await load(); }}
        />
      )}
    </div>
  );
}

function TestCard({ test, results, onOpen, onLog }) {
  const best = useMemo(() => {
    if (!results?.length) return null;
    // For time-based tests lower is better; for others, higher. Simple heuristic: prefer max unless unit is time-like.
    const timeUnits = ["s", "sec", "ms", "min"];
    const isTime = timeUnits.includes((results[0]?.unit || "").toLowerCase());
    const sorted = [...results].sort((a, b) => isTime ? a.value - b.value : b.value - a.value);
    return sorted[0];
  }, [results]);

  return (
    <div
      className="border border-white/10 bg-white/[0.02] hover:border-white/20 transition group"
      data-testid={`test-card-${test.test_id}`}
    >
      <button onClick={onOpen} className="w-full text-left p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="font-heading text-sm flex-1">{test.name}</div>
          {test.video_url && <VideoCamera size={14} weight="duotone" className="text-brand-accent shrink-0" />}
        </div>
        {test.protocol && (
          <p className="text-xs text-text-secondary line-clamp-2">{test.protocol}</p>
        )}
        {best ? (
          <div className="pt-2 border-t border-white/5 flex items-baseline justify-between">
            <div>
              <div className="label-tiny text-text-secondary">MY BEST</div>
              <div className="font-heading text-lg tabular-nums text-brand-accent">
                {best.value} {best.unit || ""}
              </div>
            </div>
            <div className="label-tiny text-text-secondary tabular-nums">
              {results.length} LOG{results.length === 1 ? "" : "S"}
            </div>
          </div>
        ) : (
          <div className="pt-2 border-t border-white/5 label-tiny text-text-secondary">NO RESULTS YET</div>
        )}
      </button>
      <div className="border-t border-white/5 px-4 py-2 flex justify-end">
        <button
          onClick={(e) => { e.stopPropagation(); onLog(); }}
          className="text-xs label-tiny tracking-widest text-brand-accent hover:text-heat transition-colors inline-flex items-center gap-1"
          data-testid={`test-log-btn-${test.test_id}`}
        >
          <Plus size={12} weight="bold" /> LOG SCORE
        </button>
      </div>
    </div>
  );
}

function TestDetailModal({ test, results, onClose, onLog, onDeleteResult }) {
  const media = resolveMedia(test.video_url);
  const isSearch = media.includes("results?search_query");
  const embed = !isSearch && isVideoHostUrl(media);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="test-detail-modal">
      <div className="bg-surface-DEFAULT border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-white/10 flex items-start justify-between">
          <div>
            <div className="label-tiny text-brand-accent">// {(test.category || "").replace("_", " ").toUpperCase()}</div>
            <h2 className="font-heading text-2xl mt-1">{test.name}</h2>
            {test.sport && <div className="label-tiny text-text-secondary">SPORT: {test.sport}</div>}
          </div>
          <button onClick={onClose} className="text-text-secondary hover:text-heat" data-testid="test-detail-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {media && isSearch && (
            <a href={media} target="_blank" rel="noreferrer" className="relative w-full aspect-video border border-white/10 flex flex-col items-center justify-center gap-2 group bg-black/40" data-testid="test-watch-youtube">
              <div className="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play size={26} weight="fill" className="text-white ml-1" />
              </div>
              <span className="label-tiny text-white tracking-widest">WATCH ON YOUTUBE</span>
            </a>
          )}
          {media && embed && (
            <div className="relative w-full aspect-video border border-white/10">
              <iframe src={media} title={test.name} className="w-full h-full" allowFullScreen allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" />
            </div>
          )}
          {test.protocol && (
            <div>
              <div className="label-tiny text-brand-accent mb-1">// PROTOCOL</div>
              <p className="text-sm whitespace-pre-line">{test.protocol}</p>
            </div>
          )}
          {test.notes && (
            <div>
              <div className="label-tiny text-brand-accent mb-1">// NOTES</div>
              <p className="text-sm whitespace-pre-line text-text-secondary">{test.notes}</p>
            </div>
          )}

          <div className="border-t border-white/5 pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="label-tiny text-brand-accent">// MY RESULTS ({results.length})</div>
              <button onClick={onLog} className="btn-kinetic px-3 py-1.5 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5" data-testid="test-detail-log-btn">
                <Plus size={12} weight="bold" /> LOG SCORE
              </button>
            </div>
            {results.length === 0 ? (
              <div className="text-xs text-text-secondary py-4 text-center">
                No results yet. Log your first score to start tracking.
              </div>
            ) : (
              <div className="space-y-1 max-h-56 overflow-y-auto no-scrollbar">
                {results.map((r) => (
                  <div key={r.result_id} className="flex items-center justify-between border border-white/5 px-3 py-2" data-testid={`result-row-${r.result_id}`}>
                    <div className="min-w-0">
                      <div className="font-heading text-sm tabular-nums">{r.value} {r.unit || ""}</div>
                      {r.notes && <div className="label-tiny text-text-secondary truncate">{r.notes}</div>}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="label-tiny text-text-secondary tabular-nums">{r.date}</div>
                      <button onClick={() => onDeleteResult(r.result_id)} className="text-text-secondary hover:text-red-500" data-testid={`result-delete-${r.result_id}`}>
                        <Trash size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

